<template>
  <div class="TransferBots">
    <header class="TransferBots__Header">
      <h1>סטטוס בוטי הסבות</h1>
      <div class="TransferBots__Header__Meta" v-if="lastPollAt">
        עודכן: {{ formatDateTimeWithSeconds(lastPollAtIso) }}
      </div>
      <div class="TransferBots__Header__Actions">
        <b-button
          type="is-success"
          icon-left="play"
          :loading="bulkActionInFlight === 'start'"
          :disabled="!ready || bulkActionInFlight !== null"
          @click="handleStartAll"
        >הפעל הכל</b-button>
        <b-button
          type="is-danger"
          icon-left="stop"
          :loading="bulkActionInFlight === 'stop'"
          :disabled="!ready || bulkActionInFlight !== null"
          @click="handleStopAll"
        >עצור הכל</b-button>
      </div>
    </header>

    <section class="TransferBots__Cubes">
      <div
        v-for="bot in visibleCubes"
        :key="bot.key"
        class="Cube"
        :class="cubeColorClass(bot)"
      >
        <header class="Cube__Header">
          <span class="Cube__Dot"></span>
          <span class="Cube__Name">{{ bot.label }}</span>
          <span class="Cube__State">{{ stateLabel(bot) }}</span>
          <b-dropdown
            v-if="canStart(bot) || canStop(bot)"
            position="is-bottom-left"
            aria-role="list"
          >
            <template #trigger="{ active }">
              <b-button
                size="is-small"
                :icon-right="active ? 'menu-up' : 'dots-vertical'"
                :loading="actionInFlight === bot.key"
                class="Cube__MenuBtn"
              />
            </template>
            <b-dropdown-item
              v-if="canStart(bot)"
              aria-role="listitem"
              @click="handleStart(bot)"
            >הפעל</b-dropdown-item>
            <b-dropdown-item
              v-if="canStop(bot)"
              aria-role="listitem"
              @click="handleStop(bot)"
            >עצור</b-dropdown-item>
            <b-dropdown-item
              v-if="bot.supportsRestart && canStop(bot)"
              aria-role="listitem"
              @click="handleRestart(bot)"
            >הפעל מחדש</b-dropdown-item>
          </b-dropdown>
        </header>
        <div class="Cube__Subtitle" v-if="bot.municipalities">
          {{ bot.municipalities.join(', ') }}
        </div>

        <div class="Cube__Body" v-if="bot.hostReachable && bot.status">
          <div class="Cube__Row" v-if="bot.status.currentReportNumber">
            <span>דוח נוכחי:</span>
            <strong>{{ bot.status.currentReportNumber }}</strong>
          </div>
          <div class="Cube__Row" v-if="bot.status.transfersCompleted != null">
            <span>הושלמו:</span>
            <strong>{{ bot.status.transfersCompleted }}</strong>
          </div>
          <div class="Cube__Row" v-if="bot.status.transfersFailed != null">
            <span>נכשלו:</span>
            <strong>{{ bot.status.transfersFailed }}</strong>
          </div>
          <div class="Cube__Row Cube__Row--date" v-if="bot.status.startedAt">
            <span>התחלה:</span>
            <strong>{{ formatDateTime(bot.status.startedAt) }}</strong>
          </div>
          <div class="Cube__Row Cube__Row--date" v-if="bot.status.lastActivity">
            <span>פעילות אחרונה:</span>
            <strong>{{ formatDateTime(bot.status.lastActivity) }}</strong>
          </div>
          <div class="Cube__Row Cube__Row--date" v-if="bot.status.nextPollAt">
            <span>הרצה הבאה:</span>
            <strong>{{ countdown(bot.status.nextPollAt) }}</strong>
          </div>
          <div class="Cube__Row" v-if="bot.status.waitingForHumanReason">
            <span>סיבה:</span>
            <strong>{{ bot.status.waitingForHumanReason }}</strong>
          </div>
        </div>

        <div class="Cube__Body Cube__Body--Unreachable" v-else>
          הבוט אינו מגיב כרגע.
        </div>

      </div>

      <div
        v-if="!sharedServiceReachable"
        class="Cube Cube--SharedDown"
      >
        <header class="Cube__Header">
          <span class="Cube__Dot"></span>
          <span class="Cube__Name">שירות הבוטים המשותף</span>
          <span class="Cube__State">לא זמין</span>
        </header>
        <div class="Cube__Body">{{ sharedDownTooltip }}</div>
      </div>
    </section>

    <section class="TransferBots__Api">
      <div class="TransferBots__Api__Header">
        <h2>הסבות API</h2>
        <b-button
          type="is-danger"
          icon-left="stop"
          :loading="apiActionInFlight === 'stop'"
          :disabled="apiActionInFlight !== null"
          @click="handleStopApiTransfers"
        >הפסק הכל</b-button>
      </div>
      <p class="TransferBots__Api__Description">
        הסבות API הן הסבות אשר מוגדרות לצאת דרך API ואין דרך לדעת את הסטטוס עליהן.
        אם לא בטוחים עדיף להפסיק הכל ולהפעילן שוב כדי לנקות את רשימת ההסבות.
      </p>
      <div class="ApiRow" v-for="row in apiTransferRows" :key="row.type">
        <span class="ApiRow__Label">{{ row.label }}</span>
        <div class="ApiRow__Actions">
          <b-button
            type="is-success"
            icon-left="play"
            :loading="apiActionInFlight === `start-${row.type}`"
            :disabled="apiActionInFlight !== null"
            @click="handleStartApiTransfers(row.type)"
          >הפעל</b-button>
        </div>
      </div>
    </section>

    <section class="TransferBots__Rdp">
      <h2>חיבור למכונת הסבות</h2>
      <b-tabs v-model="activeRdpTab" type="is-boxed">
        <b-tab-item
          v-for="session in rdpSessions"
          :key="session.key"
          :label="session.label"
        >
          <div class="RdpTab">
            <p>פתח את שולחן העבודה המרוחק בלשונית חדשה.</p>
            <b-button
              type="is-primary"
              icon-left="open-in-new"
              tag="a"
              :href="session.url"
              target="_blank"
              rel="noopener noreferrer"
            >פתח חיבור - {{ session.label }}</b-button>
          </div>
        </b-tab-item>
      </b-tabs>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import rdpSessions from '@/config/rdpSessions';
import ReportService from '@/services/ReportService';
import { formatDateTime, formatDateTimeWithSeconds, getCountdown } from '@/utils/dateFormat';
import { BOT_STATE, BOT_HOSTS } from '@/store/modules/transferBotsStore';

const TRANSFER_TYPE_CLIENTS = 1;
const TRANSFER_TYPE_EMPLOYEES = 2;

const API_TRANSFER_ROWS = [
  { type: TRANSFER_TYPE_CLIENTS, label: 'הסבת לקוחות' },
  { type: TRANSFER_TYPE_EMPLOYEES, label: 'הסבת עובדים' },
];

export default {
  name: 'TransferBots',
  data() {
    return {
      rdpSessions,
      activeRdpTab: 0,
      actionInFlight: null,
      bulkActionInFlight: null,
      apiActionInFlight: null,
      apiTransferRows: API_TRANSFER_ROWS,
      now: Date.now(),
      countdownTimer: null,
    };
  },
  computed: {
    ...mapGetters('transferBots', ['bots', 'sharedServiceReachable']),
    ...mapState('transferBots', ['lastPollAt']),
    visibleCubes() {
      if (this.sharedServiceReachable) return this.bots;
      return this.bots.filter((b) => b.host !== BOT_HOSTS.SHARED);
    },
    sharedDownTooltip() {
      const names = this.bots
        .filter((b) => b.host === BOT_HOSTS.SHARED)
        .map((b) => b.label)
        .join(', ');
      return `מושפעים: ${names}`;
    },
    lastPollAtIso() {
      return this.lastPollAt ? new Date(this.lastPollAt).toISOString() : null;
    },
    ready() {
      return this.lastPollAt !== null;
    },
    bulkStartCandidates() {
      return this.bots.filter((b) => !b.excludeFromBulk
        && b.hostReachable
        && (!b.status || b.status.state === BOT_STATE.Stopped));
    },
    bulkStopCandidates() {
      return this.bots.filter((b) => !b.excludeFromBulk
        && b.hostReachable
        && b.status
        && (b.status.state === BOT_STATE.Working
          || b.status.state === BOT_STATE.Starting
          || b.status.state === BOT_STATE.WaitingForHuman));
    },
  },
  created() {
    this.startPolling();
    this.countdownTimer = setInterval(() => { this.now = Date.now(); }, 1000);
  },
  beforeDestroy() {
    this.stopPolling();
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  },
  methods: {
    ...mapActions('transferBots', ['startPolling', 'stopPolling', 'startBot', 'stopBot', 'restartBot']),
    formatDateTime,
    formatDateTimeWithSeconds,
    countdown(iso) { return getCountdown(iso, this.now); },
    stateLabel(bot) {
      if (!bot.hostReachable) return 'לא זמין';
      if (!bot.status) return 'עצור';
      switch (bot.status.state) {
        case BOT_STATE.Working: return 'פעיל';
        case BOT_STATE.WaitingForHuman: return 'ממתין לאדם';
        case BOT_STATE.Starting: return 'מתחיל';
        case BOT_STATE.Stopped: return 'עצור';
        default: return '-';
      }
    },
    cubeColorClass(bot) {
      if (!bot.hostReachable) return 'is-danger';
      if (!bot.status) return 'is-muted'; // pre-first-poll: visually identical to Stopped
      if (bot.status.state === BOT_STATE.WaitingForHuman) return 'is-warning';
      if (bot.status.state === BOT_STATE.Working) return 'is-success';
      if (bot.status.state === BOT_STATE.Starting) return 'is-info';
      return 'is-muted';
    },
    canStart(bot) {
      if (!bot.hostReachable) return false;
      if (!bot.status) return true;
      return bot.status.state === BOT_STATE.Stopped;
    },
    canStop(bot) {
      if (!bot.hostReachable || !bot.status) return false;
      return bot.status.state === BOT_STATE.Working
        || bot.status.state === BOT_STATE.Starting
        || bot.status.state === BOT_STATE.WaitingForHuman;
    },
    async handleStart(bot) {
      this.actionInFlight = bot.key;
      try {
        await this.startBot(bot.key);
        this.$buefy.toast.open({ type: 'is-success', message: 'הבוט הופעל', position: 'is-top', duration: 3000 });
      } catch {
        this.$buefy.toast.open({ type: 'is-danger', message: 'שגיאה בהפעלת הבוט', position: 'is-top', duration: 4000 });
      } finally {
        this.actionInFlight = null;
      }
    },
    async runBulk(kind) {
      const candidates = kind === 'start' ? this.bulkStartCandidates : this.bulkStopCandidates;
      const totalEligible = this.bots.filter((b) => !b.excludeFromBulk).length;
      const skipped = totalEligible - candidates.length;
      if (candidates.length === 0) {
        this.$buefy.toast.open({
          type: 'is-info',
          message: 'אין בוטים מתאימים לפעולה',
          position: 'is-top',
          duration: 3000,
        });
        return;
      }
      this.bulkActionInFlight = kind;
      const action = kind === 'start' ? this.startBot : this.stopBot;
      const results = await Promise.allSettled(candidates.map((b) => action(b.key)));
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;
      const verb = kind === 'start' ? 'הופעלו' : 'נעצרו';
      const parts = [`${verb}: ${succeeded}`];
      if (failed) parts.push(`נכשלו: ${failed}`);
      if (skipped) parts.push(`דולגו: ${skipped}`);
      this.$buefy.toast.open({
        type: failed ? 'is-warning' : 'is-success',
        message: parts.join(', '),
        position: 'is-top',
        duration: 4000,
      });
      this.bulkActionInFlight = null;
    },
    handleStartAll() {
      this.$buefy.dialog.confirm({
        title: 'הפעלת כל הבוטים',
        message: `האם להפעיל את כל הבוטים? (${this.bulkStartCandidates.length} מתאימים)`,
        confirmText: 'כן',
        cancelText: 'לא',
        type: 'is-success',
        onConfirm: () => this.runBulk('start'),
      });
    },
    handleStopAll() {
      this.$buefy.dialog.confirm({
        title: 'עצירת כל הבוטים',
        message: `האם לעצור את כל הבוטים? (${this.bulkStopCandidates.length} מתאימים)`,
        confirmText: 'כן',
        cancelText: 'לא',
        type: 'is-danger',
        onConfirm: () => this.runBulk('stop'),
      });
    },
    handleStartApiTransfers(type) {
      const row = this.apiTransferRows.find((r) => r.type === type);
      this.$buefy.dialog.confirm({
        title: 'שידור דוחות',
        message: `האם להתחיל ${row.label}?`,
        confirmText: 'כן',
        cancelText: 'לא',
        type: 'is-success',
        onConfirm: async () => {
          this.apiActionInFlight = `start-${type}`;
          try {
            await ReportService.sendAllTransfers(type);
            this.$buefy.toast.open({ type: 'is-success', message: 'השידור החל...', position: 'is-top', duration: 4000 });
          } catch {
            this.$buefy.toast.open({ type: 'is-danger', message: 'שגיאה בהפעלת השידור', position: 'is-top', duration: 4000 });
          } finally {
            this.apiActionInFlight = null;
          }
        },
      });
    },
    async handleStopApiTransfers() {
      this.apiActionInFlight = 'stop';
      try {
        await ReportService.cancelAllTransfers();
        this.$buefy.toast.open({ type: 'is-info', message: 'השידור הפסיק...', position: 'is-top', duration: 4000 });
      } catch {
        this.$buefy.toast.open({ type: 'is-danger', message: 'שגיאה בעצירת השידור', position: 'is-top', duration: 4000 });
      } finally {
        this.apiActionInFlight = null;
      }
    },
    async handleRestart(bot) {
      this.actionInFlight = bot.key;
      try {
        await this.restartBot(bot.key);
        this.$buefy.toast.open({ type: 'is-success', message: 'הבוט הופעל מחדש', position: 'is-top', duration: 3000 });
      } catch {
        this.$buefy.toast.open({ type: 'is-danger', message: 'שגיאה בהפעלה מחדש של הבוט', position: 'is-top', duration: 4000 });
      } finally {
        this.actionInFlight = null;
      }
    },
    async handleStop(bot) {
      this.actionInFlight = bot.key;
      try {
        await this.stopBot(bot.key);
        this.$buefy.toast.open({ type: 'is-info', message: 'הבוט נעצר', position: 'is-top', duration: 3000 });
      } catch {
        this.$buefy.toast.open({ type: 'is-danger', message: 'שגיאה בעצירת הבוט', position: 'is-top', duration: 4000 });
      } finally {
        this.actionInFlight = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.TransferBots {
  padding: 24px;

  &__Header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;

    h1 { font-size: 22px; font-weight: 600; }
  }

  &__Header__Meta { font-size: 12px; color: #888; }

  &__Header__Actions {
    display: flex;
    gap: 8px;
    margin-inline-start: auto;
  }

  &__Cubes {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 18px;
    margin-bottom: 32px;
  }

  &__Api {
    margin-bottom: 32px;
    h2 { font-size: 18px; }
  }

  &__Api__Header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 8px;
    h2 { flex: 1; }
  }

  &__Api__Description {
    color: #666;
    font-size: 13px;
    margin-bottom: 12px;
    line-height: 1.5;
  }

  &__Rdp { margin-top: 16px; h2 { font-size: 18px; margin-bottom: 10px; } }
}

.ApiRow {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 8px;
  padding: 12px 18px;
  margin-bottom: 10px;

  &__Label { font-weight: 600; font-size: 15px; flex: 1; }
  &__Actions { display: flex; gap: 8px; }
}

.Cube {
  background: #fff;
  border: 1px solid #dbdbdb;
  border-top: 4px solid #b5b5b5;
  border-radius: 8px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__Header {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  &__Dot {
    width: 12px; height: 12px; border-radius: 50%;
    background: #b5b5b5;
    flex-shrink: 0;
  }
  &__Name { font-weight: 600; font-size: 17px; flex: 1; }
  &__State { font-size: 13px; color: #666; }
  &__Subtitle { font-size: 12px; color: #888; margin-top: -8px; }

  &__Body {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 14px;
    &--Unreachable { color: #f14668; }
  }
  &__Row {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    span { color: #777; }
    strong { font-weight: 600; }
    &--date strong { direction: ltr; color: #666; font-weight: 500; }
  }

  ::v-deep .Cube__MenuBtn {
    background: transparent;
    border: 0;
    box-shadow: none;
    color: #888;
    &:hover { background: #f0f0f0; color: #333; }
  }

  &.is-success { border-top-color: #48c774; .Cube__Dot { background: #48c774; } }
  &.is-warning { border-top-color: #ffdd57; .Cube__Dot { background: #ffdd57; } }
  &.is-danger  { border-top-color: #f14668; .Cube__Dot { background: #f14668; } }
  &.is-info    { border-top-color: #3e8ed0; .Cube__Dot { background: #3e8ed0; } }
  &.is-muted   { border-top-color: #b5b5b5; .Cube__Dot { background: #b5b5b5; } }

  &--SharedDown {
    grid-column: span 2;
    border-color: #f14668;
    border-top-color: #f14668;
    .Cube__Dot { background: #f14668; }
  }
}

.RdpTab {
  display: flex; flex-direction: column; align-items: flex-start; gap: 12px; padding: 16px 4px;
  p { color: #666; font-size: 13px; }
}
</style>
